import React, { FC, ReactElement, useLayoutEffect, useRef } from 'react'
import styles from './coverContainer.m.scss'
import clsx from 'clsx'
import { SrcSetImage } from 'components/cover/srcSetImage'

export interface CoverContainerProps {
  /** An optional caption for the cover image */
  caption?: ReactElement

  /** Height for the cover, best expressed in vh. */
  coverHeight?: string

  /** Should the title be displayed inverted */
  inverted?: boolean

  /** The padding for the mobile title */
  mobilePadding?: string

  /** Padding from top or bottom */
  mobilePosition?: 'top' | 'bottom'

  /** Desktop padding */
  padding?: string

  /** Padding from top or bottom */
  position?: 'top' | 'bottom'

  verticalPosition?: 'left' | 'right'

  verticalPadding?: string

  /** The poster image to use */
  poster: SrcSetImage
  portraitPoster: SrcSetImage

  /** Use a text shadow on the title for additional contrast */
  showTextShadow?: boolean

  /** Use a gradient at the top for additional contrast for the masthead */
  showTopGradient?: boolean

  /** The subtitle */
  subtitle: ReactElement

  /** The title */
  title: ReactElement
}

export const CoverContainer: FC<CoverContainerProps> = ({
  children,
  caption,
  coverHeight = '66vh',
  inverted = false,
  padding = '3ex',
  position = 'top',
  verticalPosition = 'left',
  verticalPadding = '0',
  mobilePadding = padding,
  mobilePosition = position,
  poster,
  portraitPoster = poster,
  showTextShadow = true,
  showTopGradient = true,
  subtitle,
  title
}) => {
  const placeholderColorString = `rgb(${poster.placeholder.color
    .slice(0, 3)
    .join(',')})`
  const transparentPlaceholderColorString = `rgba(${poster.placeholder.color
    .slice(0, 3)
    .join(',')},0)`

  const isInPortrait = window.matchMedia('orientation: portrait').matches
  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode

  const containerElement = useRef(null)

  const textShadow = showTextShadow
    ? `0 0 2em ${placeholderColorString}, 0 0 0.75em ${placeholderColorString}`
    : 'none'

  useLayoutEffect(() => {
    containerElement.current.style.setProperty(
      '--cover-vertical-padding',
      verticalPadding
    )
    containerElement.current.style.setProperty(
      '--cover-horizontal-padding',
      padding
    )
    containerElement.current.style.setProperty(
      '--cover-horizontal-padding-portrait',
      mobilePadding
    )
  }, [verticalPadding, containerElement.current])

  return (
    <>
      <div
        ref={containerElement}
        className={clsx(styles.coverContainer, isIE11 && styles.ie11)}
        style={{
          backgroundColor: placeholderColorString,
          backgroundImage: `url(${
            isInPortrait
              ? portraitPoster.placeholder.url
              : poster.placeholder.url
          })`,
          height: coverHeight
        }}
      >
        {children}

        {showTopGradient && (
          <div
            className={styles.topGradient}
            style={{
              background: `linear-gradient(${placeholderColorString}, ${transparentPlaceholderColorString})`
            }}
          />
        )}

        <div
          className={styles.titleBox}
          style={{
            [position]: 'var(--cover-horizontal-padding)',
            textShadow: textShadow
          }}
        >
          <div className='ui container'>
            <div
              style={{
                ['padding-' + verticalPosition]: 'var(--cover-vertical-padding)'
              }}
            >
              <h1 className={clsx(styles.header, inverted && 'inverted')}>
                {title}
              </h1>
              <h2 className={clsx(styles.lead, inverted && 'inverted')}>
                {subtitle}
              </h2>
            </div>
          </div>
        </div>
      </div>
      {caption && (
        <aside className='ui container'>
          <div className={styles.caption}>{caption}</div>
        </aside>
      )}
    </>
  )
}
